import { useEffect, useState } from "react";
import { AdvertisementTypeEnum, Name } from "@app/types/enums";
import Advertisement from "@components/Advertisement/Advertisement";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import MetaTags from "@components/MetaTags/MetaTags";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import ArticleAttachmentsFromDrupal from "@pages/Article/components/ArticleAttachmentsFromDrupal";
import ArticleAuthorsList from "@pages/Article/components/ArticleAuthorsList";
import ArticleKeywords, {
  KeywordDisplayOption,
} from "@pages/Article/components/ArticleKeywords";
import ArticleKicker from "@pages/Article/components/ArticleKicker";
import ArticleMainImage from "@pages/Article/components/ArticleMainImage/ArticleMainImage";
import ArticleParagraphs from "@pages/Article/components/ArticleParagraphs";
import ArticlePurchaseLink from "@pages/Article/components/ArticlePurchaseLink";
import ArticleSocialSharing from "@pages/Article/components/ArticleSocialSharing";
import ArticleStockSidebar from "@pages/Article/components/ArticleStockSidebar/ArticleStockSidebar";
import ArticleSubscriberLabel from "@pages/Article/components/ArticleSubscriberLabel";
import ArticleSubshare from "@pages/Article/components/ArticleSubshare/ArticleSubshare";
import ArticleSubshareGiftingUpdate from "@pages/Article/components/ArticleSubshare/ArticleSubshareGiftingUpdate";
import ArticleSubshareSent from "@pages/Article/components/ArticleSubshare/ArticleSubshareSent";
import OutBrainWidget from "@pages/Article/components/OutBrainWidget";
import RelatedArticles from "@pages/Article/components/RelatedArticles/RelatedArticles";
import TelegramBanner from "@pages/Article/components/TelegramBanner";
import { LambdaResponsePayload } from "@pages/Article/types/Alacrity";
import { ArticleProps } from "@pages/Article/types/Article";
import { checkDisplayPaywall } from "@pages/Article/utils/helpers";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { CDP_PAYWALL_MODULE_ID } from "@util/constant";
import {
  getArticleBlurb,
  getArticleJSONLD,
  getFieldValue,
  getKickerFromArticleDataObject,
  getStockFromArticleDataObject,
  GoogleAdsSlotFactory,
} from "@util/helpers";
import cx from "classnames";
import Cookies from "js-cookie";
import { find, isEmpty } from "lodash-es";

export default function ArticleDisplayLifestyle({
  article,
  isGiftReceived,
  seeAlso,
  variant,
  index,
}: ArticleProps): React.ReactElement {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const isNotified = Cookies.get("giftUpdateNotified") === "yes";
  const [showGiftNotification, setShowGiftNotification] = useState(false);
  const [showGiftSent, setShowGiftSent] = useState(false);
  const [alacrityRes, setAlacrityRes] = useState<LambdaResponsePayload>();

  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    updated: publishedDate,
    edited: updatedDate,
    media,
    authors,
    sections,
    tags,
    sanitizedRelatedStories,
    paywall,
    displaySetting,
    embedCodes,
    elements,
    relatedContributorProfile,
  } = article;

  const mainSection = sections?.at(0);
  const kicker = getKickerFromArticleDataObject(article);
  const contentAccess = paywall?.contentAccess || "0";
  const displayPaywall = checkDisplayPaywall({
    isGiftReceived,
    variant: variant,
    contentAccess: contentAccess,
  });
  const showRelatedLinksAndNewsletterSignupForm =
    displaySetting?.showRelatedLinksAndNewsletterSignupForm || false;

  const standFirstField =
    find(elements, {
      type: "standfirst",
    })?.fields || [];
  const standFirst = getFieldValue(standFirstField, Name.Standfirst);
  const stockCodes = getStockFromArticleDataObject(article);
  const isPremium = contentAccess === "1";
  const isSubscriber = OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  useGenerateGiftURL(
    isPremium,
    isSubscriber,
    setAlacrityRes,
    OKTAUserInfo?.mysphw
  );

  useEffect(() => {
    if (isPremium && isSubscriber && !isNotified) setShowGiftNotification(true);
  }, [isPremium, isSubscriber, isNotified, setShowGiftNotification]);

  return (
    <>
      <ArticleSubshareGiftingUpdate
        alacrityRes={alacrityRes}
        isModalOpen={showGiftNotification}
        setIsModalOpen={setShowGiftNotification}
        setShowGiftSent={setShowGiftSent}
      />

      <ArticleSubshareSent
        alacrityRes={alacrityRes}
        isModalOpen={showGiftSent}
        setIsModalOpen={setShowGiftSent}
      />

      <article
        className="display-type--lifestyle w-full font-pt-sans"
        data-index={index}
        data-cueid={id}
      >
        <MetaTags
          title={title}
          description={getArticleBlurb(article)}
          keywords={article?.tags?.map((tag) => tag.name).join(", ")}
          slug={article.urlPath}
          shortLink={"/article/" + article.id}
          canonicalUrl={article?.seoFields?.canonicalUrl}
          imageUrl={
            article?.media?.[0]?.content?.fields?.["original-caas"]?.url
          }
          imageAlt={
            article?.media?.[0]?.summary?.find(
              (summaryItem) => summaryItem.key === "alttext"
            )?.value
          }
          jsonLd={getArticleJSONLD(article)}
          robots={"index, follow, max-image-preview:large"}
        />

        <div className="relative">
          {media && !isEmpty(media) ? (
            <>
              <ArticleMainImage
                media={media}
                imgRootClassName="mx-auto max-h-[17rem] md:max-h-[29rem] lg:max-h-[50rem] lg:max-w-[1200px]"
                noCaption
                thumbnailRootClassName="bg-gray-250 flex items-center w-full justify-center h-full"
                landscapeThumbnailRootClassName="bg-gray-250 w-full mx-auto aspect-3x2 h-full lg:max-h-[50rem] flex justify-center items-center"
                landscapeImgRootClassName="w-full lg:max-w-[1400px] mx-auto lg:max-h-[50rem] "
              />
            </>
          ) : null}

          <div
            className={cx("relative w-full", {
              "-top-16 md:absolute md:bottom-0 md:top-[unset]":
                media && !isEmpty(media),
            })}
          >
            <Container rootClassName="pt-4">
              <div className="mx-auto w-10/12 bg-white p-3 lg:w-full">
                <div className="flex flex-wrap items-center space-x-1">
                  <ArticleKicker kicker={kicker} />

                  {contentAccess === "1" && kicker ? (
                    <div className="font-poppins text-4xs tracking-[1px] text-gray-650">
                      ·
                    </div>
                  ) : null}

                  <ArticleSubscriberLabel isPremium={contentAccess === "1"} />
                </div>

                <h1
                  data-testid="article-title"
                  className="mb-6 font-pt-serif text-8xl font-bold leading-normal text-black md:text-12xl"
                >
                  {title}
                </h1>
              </div>
            </Container>
          </div>
        </div>

        <Container>
          <>
            {!media ? (
              <div className="-mx-3">
                <div className="mx-auto w-10/12 p-3 lg:w-full">
                  {contentAccess === "1" ? (
                    <div className="flex items-center">
                      <ArticleSubscriberLabel isPremium />

                      <ArticleSubshare
                        isPremium={isPremium}
                        userType={OKTAUserInfo?.usertype}
                        setShowGiftSent={setShowGiftSent}
                      />
                    </div>
                  ) : null}

                  <ArticleKicker kicker={kicker} />

                  <h1
                    data-testid="article-title"
                    className="font-pt-serif text-8xl font-bold leading-normal text-black md:text-12xl"
                  >
                    {title}
                  </h1>

                  <>
                    {standFirst ? (
                      <p className="mb-6 mt-2 font-poppins">{standFirst}</p>
                    ) : null}
                  </>
                </div>
              </div>
            ) : null}
          </>

          <Row rootClassName="-mt-16 md:mt-0">
            <Column rootClassName="w-10/12 py-3 lg:w-8/12">
              <>
                {publishedDate && updatedDate ? (
                  <ArticleAuthorsList
                    rootClassName="mb-6"
                    authors={authors}
                    contributors={relatedContributorProfile}
                    updatedDate={updatedDate}
                    publishedDate={publishedDate}
                    hasUpdatedTimeDisplayed={
                      displaySetting?.hasUpdatedTimeDisplayed
                    }
                  />
                ) : null}
              </>

              <div className="mb-6 flex items-center justify-between border-b border-t border-gray-250 py-4">
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.First}
                  />
                ) : null}

                <div className="flex space-x-3">
                  <ArticleSocialSharing articleTitle={title} />

                  <ArticleSubshare
                    isPremium={isPremium}
                    userType={OKTAUserInfo?.usertype}
                    setShowGiftSent={setShowGiftSent}
                  />
                </div>
              </div>

              <ArticleAttachmentsFromDrupal
                cueId={article.id}
                rootClassName="mb-6"
              />

              <>
                {elements ? (
                  <ArticleParagraphs
                    id={id}
                    content={elements}
                    seeAlso={seeAlso}
                    embedCodes={embedCodes}
                    displayPaywall={displayPaywall}
                    displayRelatedLinksAndNewsletterSignupForm={
                      showRelatedLinksAndNewsletterSignupForm
                    }
                    rootClassName="text-lg"
                    keywords={tags}
                    sections={sections}
                    index={index}
                  />
                ) : null}
              </>

              <>
                {contentAccess !== "1" ? (
                  <CASBlock cdpId={CDP_PAYWALL_MODULE_ID} index={index} />
                ) : null}
              </>

              <>
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.Rest}
                  />
                ) : null}
              </>

              <RelatedArticles articles={sanitizedRelatedStories} />

              <TelegramBanner />
            </Column>

            <Column rootClassName="w-10/12 py-3 lg:w-4/12">
              <>
                {mainSection ? (
                  <div className="lg:h-[1050px]">
                    <Advertisement
                      rootClassName="lg:sticky lg:top-[120px] mb-5"
                      adUnitProps={{
                        id:
                          index !== 0 ? `dfp-ad-imu2-${index}` : `dfp-ad-imu2`,
                        type: AdvertisementTypeEnum.IMU2,
                        slot: GoogleAdsSlotFactory.imu2(mainSection.uniqueName),
                        isRefresh: index !== 0,
                      }}
                    />
                  </div>
                ) : null}
              </>

              <ArticleStockSidebar codes={stockCodes} />

              <>
                {paywall?.copyright === "SPH" && publishedDate ? (
                  <div className="mx-auto mb-6 max-w-[300px]">
                    <ArticlePurchaseLink
                      title={title}
                      publishedDate={publishedDate}
                    />
                  </div>
                ) : null}
              </>

              <>
                {mainSection ? (
                  <Advertisement
                    rootClassName="lg:sticky lg:top-[120px]"
                    adUnitProps={{
                      id: index !== 0 ? `dfp-ad-imu3-${index}` : `dfp-ad-imu3`,
                      type: AdvertisementTypeEnum.IMU3,
                      slot: GoogleAdsSlotFactory.imu3(mainSection.uniqueName),
                      isRefresh: index !== 0,
                    }}
                  />
                ) : null}
              </>
            </Column>

            <>
              {article.urlPath ? (
                <OutBrainWidget
                  articleUrl={article.urlPath}
                  widgetId={index === 0 ? "AR_4" : "AR_5"}
                />
              ) : null}
            </>
          </Row>
        </Container>
      </article>
    </>
  );
}
